import { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Globe, ExternalLink } from 'lucide-react'
import { Button } from '@/components/ui/button'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { trackFigmaPluginClick } from '@/lib/gtag'

gsap.registerPlugin(ScrollTrigger)

const LINKS = {
    docs: 'https://yamp.notion.site/Yamp-YAML-for-Presentation-280daf80a55780909dbbcf33a5d3beb9?pvs=74',
}

// Sparkle particles floating around the shine
function Sparkles() {
    const sparkles = [
        { left: '45%', bottom: '8%', size: 3, delay: 0, duration: 2.5 },
        { left: '55%', bottom: '12%', size: 2, delay: 0.3, duration: 2.8 },
        { left: '40%', bottom: '15%', size: 2.5, delay: 0.6, duration: 2.2 },
        { left: '60%', bottom: '10%', size: 2, delay: 0.9, duration: 3 },
        { left: '48%', bottom: '18%', size: 1.5, delay: 1.2, duration: 2.6 },
        { left: '52%', bottom: '6%', size: 2.5, delay: 0.4, duration: 2.4 },
        { left: '35%', bottom: '14%', size: 2, delay: 0.7, duration: 2.9 },
        { left: '65%', bottom: '16%', size: 1.5, delay: 1.0, duration: 2.3 },
        { left: '30%', bottom: '10%', size: 2, delay: 0.2, duration: 2.7 },
        { left: '70%', bottom: '8%', size: 2.5, delay: 0.5, duration: 2.5 },
    ]

    return (
        <>
            <style>{`
                @keyframes sparkle-float {
                    0%, 100% {
                        opacity: 0;
                        transform: scale(0.5) translateY(0);
                    }
                    50% {
                        opacity: 1;
                        transform: scale(1) translateY(-16px);
                    }
                }
                @keyframes sparkle-twinkle {
                    0%, 100% { opacity: 0.3; }
                    50% { opacity: 1; }
                }
            `}</style>
            {sparkles.map((s, i) => (
                <div
                    key={i}
                    className="absolute rounded-full bg-white"
                    style={{
                        left: s.left,
                        bottom: s.bottom,
                        width: s.size,
                        height: s.size,
                        boxShadow: `0 0 ${s.size * 2}px ${s.size}px rgba(255,255,255,0.6)`,
                        animation: `sparkle-float ${s.duration}s ease-in-out infinite, sparkle-twinkle ${s.duration * 0.5}s ease-in-out infinite`,
                        animationDelay: `${s.delay}s`,
                    }}
                />
            ))}
        </>
    )
}

// Shine effect with subtle animation - positioned at bottom
function ShineEffect() {
    return (
        <div className="shine-container absolute inset-0 pointer-events-none overflow-hidden">
            {/* Flicker + Prism animation keyframes */}
            <style>{`
                @keyframes shine-flicker {
                    0%, 100% {
                        filter: brightness(1) hue-rotate(0deg);
                        transform: translateX(-50%) scale(1);
                    }
                    15% {
                        filter: brightness(1.12) hue-rotate(3deg);
                        transform: translateX(-50%) scale(1.008);
                    }
                    30% {
                        filter: brightness(0.95) hue-rotate(-2deg);
                        transform: translateX(-50%) scale(0.998);
                    }
                    45% {
                        filter: brightness(1.18) hue-rotate(5deg);
                        transform: translateX(-50%) scale(1.012);
                    }
                    60% {
                        filter: brightness(1.05) hue-rotate(1deg);
                        transform: translateX(-50%) scale(1.002);
                    }
                    75% {
                        filter: brightness(1.1) hue-rotate(-3deg);
                        transform: translateX(-50%) scale(1.006);
                    }
                    90% {
                        filter: brightness(0.98) hue-rotate(2deg);
                        transform: translateX(-50%) scale(0.996);
                    }
                }
            `}</style>
            {/* Main shine image at bottom - larger for 1.5x footer */}
            <img
                src="/shine.jpg"
                alt=""
                className="shine-image absolute w-[800px] h-auto opacity-0"
                style={{
                    left: '50%',
                    bottom: -1,
                    transform: 'translateX(-50%)',
                    mixBlendMode: 'screen',
                    animation: 'shine-flicker 4s ease-in-out infinite',
                }}
            />
            {/* Sparkle particles */}
            <Sparkles />
        </div>
    )
}

export function Footer() {
    const footerRef = useRef<HTMLElement>(null)
    const ctaRef = useRef<HTMLDivElement>(null)
    const linksRef = useRef<HTMLDivElement>(null)

    const { t, i18n } = useTranslation()

    const toggleLanguage = () => {
        const newLang = i18n.language === 'ja' ? 'en' : 'ja'
        i18n.changeLanguage(newLang)
    }

    useEffect(() => {
        const ctx = gsap.context(() => {
            // Shine effect - fade in when footer becomes visible
            gsap.fromTo(
                '.shine-image',
                { opacity: 0, scale: 0.95 },
                {
                    opacity: 1,
                    scale: 1,
                    duration: 1.8,
                    ease: 'power2.out',
                    scrollTrigger: {
                        trigger: footerRef.current,
                        start: 'top 90%',
                        toggleActions: 'play none none none',
                    },
                }
            )

            // CTA reveal with stagger
            if (ctaRef.current) {
                const ctaElements = ctaRef.current.querySelectorAll('.cta-animate')
                gsap.fromTo(
                    ctaElements,
                    { opacity: 0, y: 30 },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 0.8,
                        stagger: 0.12,
                        ease: 'power3.out',
                        scrollTrigger: {
                            trigger: footerRef.current,
                            start: 'top 85%',
                            toggleActions: 'play none none none',
                        },
                    }
                )
            }

            // Links fade in
            if (linksRef.current) {
                gsap.fromTo(
                    linksRef.current,
                    { opacity: 0 },
                    {
                        opacity: 1,
                        duration: 0.6,
                        delay: 0.4,
                        ease: 'power2.out',
                        scrollTrigger: {
                            trigger: footerRef.current,
                            start: 'top 80%',
                            toggleActions: 'play none none none',
                        },
                    }
                )
            }
        }, footerRef)

        return () => ctx.revert()
    }, [])

    return (
        <footer
            ref={footerRef}
            className="relative bg-bg-base"
        >

            {/* Ambient glow from bottom */}
            <div
                className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[120%] h-[60%] pointer-events-none"
                style={{
                    background: 'radial-gradient(ellipse at bottom center, rgba(179, 58, 43, 0.08) 0%, transparent 70%)',
                }}
            />

            {/* Shine effect overlay */}
            <ShineEffect />

            <div className="container flex flex-col py-8 relative z-10">

                {/* CTA Section - Dramatic and spacious */}
                <div
                    ref={ctaRef}
                    className="flex-1 flex flex-col items-center justify-start text-center pt-8"
                >
                    <img
                        src="/logo.svg"
                        alt="digem"
                        width={96}
                        height={32}
                        className="block cta-animate opacity-0 mb-2 md:mb-6 scale-75 md:scale-100"
                    />

                    <p className="cta-animate opacity-0 font-heading-en text-[clamp(2.5rem,5vw,4rem)] text-white leading-[1.1] tracking-tight mb-8">
                        Think <span className="italic font-light text-white/80">deeper,</span>
                        <br className="sm:hidden" />
                        <span className="sm:ml-3">Fill faster</span>
                    </p>

                    <Button
                        variant="cta"
                        size="lg"
                        className="cta-animate opacity-0"
                        asChild
                    >
                        <a
                            href="https://www.figma.com/community/plugin/1557378742956260910"
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => trackFigmaPluginClick('footer_cta')}
                        >
                            {t('cta.startDigging')}
                        </a>
                    </Button>
                </div>
                <div className="mt-40">
                    <div className="flex flex-row items-start md:items-end justify-between gap-4">
                        <a
                            href={LINKS.docs}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-bg-base/75 md:bg-white/10 border border-white/15 md:border-white/[0.06] hover:bg-white/[0.06] hover:border-white/[0.10] transition-all group"
                        >
                            <span className="font-body-en text-sm text-white/80 group-hover:text-white transition-colors">
                                {t('footer.documentation')}
                            </span>
                            <ExternalLink className="w-3 h-3 text-white/80 group-hover:text-brand-accent transition-colors" />
                        </a>
                        <button
                            onClick={toggleLanguage}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-bg-base/75 md:bg-white/10 border border-white/15 md:border-white/[0.06] hover:bg-white/[0.06] hover:border-white/[0.10] transition-all group"
                        >
                            <Globe className="w-3.5 h-3.5 text-white/80 group-hover:text-brand-accent transition-colors" />
                            <span className="text-sm text-white/80 group-hover:text-white transition-colors">
                                {t('footer.language')}
                            </span>
                        </button>
                    </div>
                </div>

            </div>
            {/* Footer Links - positioned to avoid light overlay */}
            <div className="py-1 bg-bg-base -mb-8">
                <div className="container">
                    {/* Legal Links */}
                    <div className="w-full flex items-center justify-between">
                        <div className="flex items-center justify-start">
                            <Link
                                to="/privacy"
                                className="font-body-en text-[10px] md:text-xs text-white/80 hover:text-white transition-colors p-1 pl-0"
                            >
                                {t('footer.privacy')}
                            </Link>

                            <Link
                                to="/terms"
                                className="font-body-en text-[10px] md:text-xs text-white/80 hover:text-white transition-colors p-1"
                            >
                                {t('footer.terms')}
                            </Link>

                            <Link
                                to="/contact"
                                className="font-body-en text-[10px] md:text-xs text-white/80 hover:text-white transition-colors p-1"
                            >
                                {t('footer.contact')}
                            </Link>

                        </div>
                        <p className="font-body-en text-[10px] md:text-xs tracking-wider text-white/50 p-1 pr-0">
                            {t('footer.copyright')}
                        </p>
                    </div>
                </div>
            </div>
        </footer >
    )
}
